// IndexedDB-backed cache of recent emails so search, folder-open, and
// re-opening a message feel instant. The cache stores everything
// MessageList + ReadingPane need to render the first frame (metadata +
// stripped body text), but NOT bodyHtml / attachments / inline images —
// those stay server-side and are fetched on demand.
//
// Retention: 14 days. Safety cap: 10 000 rows. prune() is idempotent and
// called once on mount.

import { openDB } from 'idb';
import { mailboxScope } from './impersonate';

const DB_NAME = 'onpoint-mail';
const DB_VERSION = 2;   // v2: compound [folder_id, date] index (Section 6 #5)
const STORE = 'messages';
const RETENTION_DAYS = 14;
const MAX_ROWS = 10_000;
const MAX_BODY_CHARS = 30_000;

// One database per mailbox: the person's own is DB_NAME, a shared mailbox
// (or an admin's view of a colleague) is DB_NAME@@<mailbox>. Folder ids are
// per-mailbox anyway, but searchLocal() reads EVERY row — with one database a
// search in Sales showed Cara's own mail too. Chosen per call, so it follows
// a switch between mailboxes without a reload.
const dbPromises = new Map();
function db() {
  const name = DB_NAME + mailboxScope();
  let dbPromise = dbPromises.get(name);
  if (!dbPromise) {
    dbPromise = openDB(name, DB_VERSION, {
      upgrade(d, oldVersion, newVersion, tx) {
        let store;
        if (!d.objectStoreNames.contains(STORE)) {
          store = d.createObjectStore(STORE, { keyPath: 'id' });
          store.createIndex('folder_id',     'folder_id');
          store.createIndex('date',          'date');
          store.createIndex('conversationId','conversationId');
          store.createIndex('updatedAt',     'updatedAt');
        } else {
          store = tx.objectStore(STORE);   // v1 store already exists — just add the new index
        }
        // v2 (Section 6 #5): compound index so a folder's rows can be read
        // newest-first via a reverse cursor with a limit — no full-folder load
        // + JS sort. IndexedDB back-fills it from existing rows on upgrade.
        if (!store.indexNames.contains('folder_date')) {
          store.createIndex('folder_date', ['folder_id', 'date']);
        }
      },
    }).catch(err => {
      // Private-browsing mode or quota refused — degrade silently. All
      // cache ops become no-ops and the app falls back to server fetches.
      // eslint-disable-next-line no-console
      console.warn('mailCache: IndexedDB unavailable —', err?.message || err);
      return null;
    });
    dbPromises.set(name, dbPromise);
  }
  return dbPromise;
}

// Normalise whatever shape the caller has (Graph-normalised message,
// search result, poll-merged row) into the cache's canonical shape.
function normalise(msg, folderId) {
  const out = {
    id:              msg.id,
    folder_id:       folderId || msg.folder_id || msg.parentFolderId || null,
    conversationId:  msg.conversationId || null,
    from:            msg.from || '',
    fromName:        msg.fromName || '',
    to:              msg.to || '',
    subject:         msg.subject || '',
    date:            msg.date || null,
    isRead:          !!msg.isRead,
    flagged:         !!msg.flagged,
    completed:       !!msg.completed,
    hasAttachments:  !!msg.hasAttachments,
    importance:      msg.importance || 'normal',
    label:           msg.label || null,
    updatedAt:       Date.now(),
  };
  if (typeof msg.bodyText === 'string') {
    out.bodyText = msg.bodyText.slice(0, MAX_BODY_CHARS);
  }
  // Only set replied/forwarded when the caller actually passed them.
  // List fetches include these (derived from MAPI PR_ICON_INDEX); the
  // getMessage endpoint does NOT, and spreading a `false` onto an
  // already-cached `true` was wiping the reply arrow from the UI.
  if (typeof msg.replied   !== 'undefined') out.replied   = !!msg.replied;
  if (typeof msg.forwarded !== 'undefined') out.forwarded = !!msg.forwarded;
  return out;
}

export async function upsertMany(messages, folderId) {
  if (!messages?.length) return;
  const d = await db();
  if (!d) return;
  try {
    const tx = d.transaction(STORE, 'readwrite');
    for (const msg of messages) {
      if (!msg?.id) continue;
      const prev = await tx.store.get(msg.id);
      const next = { ...(prev || {}), ...normalise(msg, folderId) };
      // Never overwrite an existing bodyText with an undefined one (list
      // fetches don't carry bodies, but earlier getMessage calls did).
      if (next.bodyText === undefined && prev?.bodyText) next.bodyText = prev.bodyText;
      await tx.store.put(next);
    }
    await tx.done;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('mailCache.upsertMany failed:', err?.message || err);
  }
}

export async function upsertOne(msg, folderId) {
  return upsertMany([msg], folderId);
}

export async function updateField(id, patch) {
  if (!id) return;
  const d = await db();
  if (!d) return;
  try {
    const tx = d.transaction(STORE, 'readwrite');
    const existing = await tx.store.get(id);
    if (existing) await tx.store.put({ ...existing, ...patch, updatedAt: Date.now() });
    await tx.done;
  } catch { /* ignore */ }
}

export async function removeOne(id) {
  if (!id) return;
  const d = await db();
  if (!d) return;
  try { await d.delete(STORE, id); } catch { /* ignore */ }
}

// Drop every cached row that belongs to a given folder. Used when the
// user empties Junk / Deleted Items — without this the next folder
// open would re-paint the deleted rows from the cache before the
// server response (now empty) lands, leaving stale messages visible.
export async function clearFolder(folderId) {
  if (!folderId) return;
  const d = await db();
  if (!d) return;
  try {
    const tx = d.transaction(STORE, 'readwrite');
    const idx = tx.store.index('folder_id');
    for (let c = await idx.openCursor(folderId); c; c = await c.continue()) {
      await c.delete();
    }
    await tx.done;
  } catch { /* ignore — worst case is a brief stale paint that the next sync corrects */ }
}

export async function getOne(id) {
  if (!id) return null;
  const d = await db();
  if (!d) return null;
  try { return await d.get(STORE, id); } catch { return null; }
}

// Rows currently cached for a folder, most recent first. Used to prime
// the message list instantly on folder switch while the real fetch runs.
export async function listRecent(folderId, limit = 200) {
  if (!folderId) return [];
  const d = await db();
  if (!d) return [];
  try {
    // Newest-first via a reverse cursor over [folder_id, date], stopping at
    // `limit` — avoids loading the whole folder into memory and JS-sorting it
    // (Section 6 #5). Rows with a null date aren't in the compound index (real
    // mail always has a date; those would still come from the server fetch).
    const range = IDBKeyRange.bound([folderId, ''], [folderId, '￿']);
    const tx = d.transaction(STORE, 'readonly');
    const out = [];
    let c = await tx.store.index('folder_date').openCursor(range, 'prev');
    while (c && out.length < limit) {
      out.push(c.value);
      c = await c.continue();
    }
    return out;
  } catch { return []; }
}

// Case-insensitive substring scan over subject + from + fromName. Minimum 2
// chars. Linear scan — fine for up to ~10 k rows. Section 6 #4: body text is
// deliberately NOT scanned — the App-side preview re-filter discards body-only
// matches anyway, so scanning bodies here was wasted work (lowercasing every
// cached body on each search). Results are unchanged; the scan is much cheaper.
export async function searchLocal(query, { limit = 50 } = {}) {
  const q = (query || '').trim().toLowerCase();
  if (q.length < 2) return [];
  const d = await db();
  if (!d) return [];
  try {
    const all = await d.getAll(STORE);
    const matches = all.filter(m =>
      (m.subject  && m.subject.toLowerCase().includes(q)) ||
      (m.from     && m.from.toLowerCase().includes(q)) ||
      (m.fromName && m.fromName.toLowerCase().includes(q))
    );
    return matches
      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')))
      .slice(0, limit);
  } catch { return []; }
}

// Drop rows older than the retention window; if we're still over the
// row cap, LRU-evict the least-recently-updated. Safe to call on mount.
export async function prune() {
  const d = await db();
  if (!d) return;
  try {
    const cutoff = Date.now() - RETENTION_DAYS * 24 * 60 * 60 * 1000;
    const tx = d.transaction(STORE, 'readwrite');
    for (let c = await tx.store.index('updatedAt').openCursor(IDBKeyRange.upperBound(cutoff)); c; c = await c.continue()) {
      await c.delete();
    }
    const remaining = await tx.store.count();
    if (remaining > MAX_ROWS) {
      let drop = remaining - MAX_ROWS;
      for (let c = await tx.store.index('updatedAt').openCursor(); c && drop > 0; c = await c.continue()) {
        await c.delete();
        drop -= 1;
      }
    }
    await tx.done;
  } catch { /* ignore */ }
}
